import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faHome, faClock, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';

interface Property {
  mlsId: string;
  address: string;
  price: number;
  beds?: number;
  baths?: number;
}

interface AppointmentModalProps {
  show: boolean;
  onHide: () => void;
  properties: Property[];
  leadId: string;
  agentId: string;
  agentName: string;
  onSuccess?: () => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  show,
  onHide,
  properties,
  leadId,
  agentId,
  agentName,
  onSuccess
}) => {
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [timePreferences, setTimePreferences] = useState({
    preferredDates: [
      { date: '', timeSlots: [] as string[], available: true }
    ],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    flexibility: 'flexible',
    notes: ''
  });
  const [buyerMessage, setBuyerMessage] = useState('');
  const [contactMethod, setContactMethod] = useState('phone');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handlePropertyToggle = (mlsId: string) => {
    setSelectedProperties(prev =>
      prev.includes(mlsId)
        ? prev.filter(id => id !== mlsId)
        : [...prev, mlsId]
    );
  };

  const handleDateChange = (index: number, date: string) => {
    const newDates = [...timePreferences.preferredDates];
    newDates[index].date = date;
    setTimePreferences({ ...timePreferences, preferredDates: newDates });
  };

  const handleTimeSlotToggle = (dateIndex: number, slot: string) => {
    const newDates = [...timePreferences.preferredDates];
    const currentSlots = newDates[dateIndex].timeSlots;
    
    if (currentSlots.includes(slot)) {
      newDates[dateIndex].timeSlots = currentSlots.filter(s => s !== slot);
    } else {
      newDates[dateIndex].timeSlots = [...currentSlots, slot];
    }
    
    setTimePreferences({ ...timePreferences, preferredDates: newDates });
  };

  const addDateOption = () => {
    setTimePreferences({
      ...timePreferences,
      preferredDates: [
        ...timePreferences.preferredDates,
        { date: '', timeSlots: [], available: true }
      ]
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedProperties.length === 0) {
      toast.error('Please select at least one property');
      return;
    }

    if (!timePreferences.preferredDates.some(d => d.date && d.timeSlots.length > 0)) {
      toast.error('Please select at least one date and time');
      return;
    }

    setSubmitting(true);
    
    try {
      // Map selected properties with details
      const selectedPropertyDetails = properties
        .filter(p => selectedProperties.includes(p.mlsId))
        .map((p, index) => ({
          mlsId: p.mlsId,
          address: p.address,
          price: p.price,
          priority: index + 1,
          notes: ''
        }));

      const response = await axiosInstance.post('/appointments/schedule', {
        leadId,
        agentId,
        properties: selectedPropertyDetails,
        timePreferences: {
          ...timePreferences,
          preferredDates: timePreferences.preferredDates.filter(d => d.date)
        },
        buyerMessage,
        contactMethod,
        contactPhone,
        contactEmail
      });

      if (response.data.success) {
        toast.success('Appointment request submitted successfully!');
        onSuccess?.();
        onHide();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit appointment request');
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = ['morning', 'afternoon', 'evening'];
  const today = new Date().toISOString().split('T')[0];

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
          Schedule Property Viewings
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info">
            {agentName} will coordinate your property viewings and contact you to confirm the exact times.
          </Alert>

          {/* Property Selection */}
          <div className="mb-4">
            <h5>
              <FontAwesomeIcon icon={faHome} className="me-2" />
              Select Properties to View
            </h5>
            {properties.map(property => (
              <Form.Check
                key={property.mlsId}
                type="checkbox"
                id={`property-${property.mlsId}`}
                label={
                  <div>
                    <strong>{property.address}</strong>
                    <span className="text-muted ms-2">
                      ${property.price.toLocaleString()}
                      {property.beds && ` • ${property.beds}bd`}
                      {property.baths && `/${property.baths}ba`}
                    </span>
                  </div>
                }
                checked={selectedProperties.includes(property.mlsId)}
                onChange={() => handlePropertyToggle(property.mlsId)}
                className="mb-2"
              />
            ))}
          </div>

          {/* Time Preferences */}
          <div className="mb-4">
            <h5>
              <FontAwesomeIcon icon={faClock} className="me-2" />
              Preferred Dates & Times
            </h5>
            {timePreferences.preferredDates.map((dateOption, index) => (
              <div key={index} className="mb-3 p-3 border rounded">
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date Option {index + 1}</Form.Label>
                      <Form.Control
                        type="date"
                        min={today}
                        value={dateOption.date}
                        onChange={(e) => handleDateChange(index, e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Time Slots</Form.Label>
                    <div>
                      {timeSlots.map(slot => (
                        <Form.Check
                          key={slot}
                          inline
                          type="checkbox"
                          id={`time-${index}-${slot}`}
                          label={slot.charAt(0).toUpperCase() + slot.slice(1)}
                          checked={dateOption.timeSlots.includes(slot)}
                          onChange={() => handleTimeSlotToggle(index, slot)}
                        />
                      ))}
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
            <Button variant="outline-primary" size="sm" onClick={addDateOption}>
              Add Another Date Option
            </Button>
          </div>

          {/* Contact Preferences */}
          <div className="mb-4">
            <h5>Contact Preferences</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Preferred Contact Method</Form.Label>
                  <Form.Select 
                    value={contactMethod} 
                    onChange={(e) => setContactMethod(e.target.value)}
                  >
                    <option value="phone">Phone</option>
                    <option value="email">Email</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {(contactMethod === 'phone' || contactMethod === 'both') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  Phone Number
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </Form.Group>
            )}

            {(contactMethod === 'email' || contactMethod === 'both') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Email Address
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="your@email.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </Form.Group>
            )}
          </div>

          {/* Additional Message */}
          <Form.Group className="mb-4">
            <Form.Label>Message for {agentName} (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Any specific requests or information about your availability..."
              value={buyerMessage}
              onChange={(e) => setBuyerMessage(e.target.value)}
            />
          </Form.Group>

          {/* Flexibility Notes */}
          <Form.Group className="mb-3">
            <Form.Label>Scheduling Notes (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 'Flexible on weekends', 'Prefer mornings'"
              value={timePreferences.notes}
              onChange={(e) => setTimePreferences({
                ...timePreferences,
                notes: e.target.value
              })}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AppointmentModal;